import { Component, ReactNode } from "react";
import { Link } from "react-router-dom";
import foundersDistribution from "../../assets/images/founders-distribution.svg";
import pieChart from "../../assets/images/tokenomics-pie-chart.svg";
import publicDistribution from "../../assets/images/public-distribution.svg";
import { LiskClient } from "../../components/clients/lisk_client";
import { NotificationMessage } from "../../components/notification/notification";
import { linearInterpolateY } from "../../components/util/helpers";
import "./tokenomics.css";

interface TokenomicsProps {
    alertCb: (alert: NotificationMessage) => void;
}

interface TokenomicsState {
    rowMarginTop: string;
    rowPaddingTop: string;
    rowPaddingBottom: string;
}

export class Tokenomics extends Component<TokenomicsProps, TokenomicsState> {
    public state: TokenomicsState = {
        rowMarginTop: "",
        rowPaddingTop: "",
        rowPaddingBottom: "",
    };

    private handleResize: () => void = () => {
        const screenWidth = window.innerWidth;
        this.setState((state) => ({
            ...state,
            rowMarginTop: this.calculateRowMarginTop(screenWidth),
            rowPaddingTop: this.calculateRowPaddingTop(screenWidth),
            rowPaddingBottom: this.calculateRowPaddingBottom(screenWidth),
        }));
    };

     public componentDidMount(): void {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !entry.target.classList.contains("show")) {
                    entry.target.classList.add("show");
                }
            });
        }, { threshold: 0.6 });
        const hiddenElements = document.querySelectorAll("#tokenomics-row .hidden");
        hiddenElements.forEach((elem) => observer.observe(elem));

        window.addEventListener("resize", this.handleResize);
        // Initialise component scaling
        this.handleResize();
    }

    public componentWillUnmount(): void {
        window.removeEventListener("resize", this.handleResize);
    }

    public render(): ReactNode {
        return (
            <div id="tokenomics-row-background" style={{
                marginTop: this.state.rowMarginTop,
                paddingTop: this.state.rowPaddingTop,
                paddingBottom: this.state.rowPaddingBottom,
            }}>
                <div className="container">
                    <div id="tokenomics-row">
                        <div id="tokenomics-header" className="hidden">
                            <h2 className="rgb-orange" style={{ textAlign: "center" }}>Token Allocation</h2>
                        </div>
                        <div id="tokenomics-content-row">
                            <img id="public-distribution-img" src={publicDistribution} alt="" className="hidden" />
                            <img id="pie-chart-img" src={pieChart} alt="" className="hidden" />
                            <img id="founders-distribution-img" src={foundersDistribution} alt="" className="hidden" />
                        </div>
                        <div id="tokenomics-button-row" className="hidden">
                            <Link to="https://oku.trade/app/lisk/pool/0x7a30b5ade7effbe52e2e1bc196a280b58d84773d" target="_blank" rel="noopener noreferrer">
                                <button className="rgb-button-blue hidden" style={{ width: "min(201px, 25.125vw)", height: "min(64px, 8vw)" }}>Buy</button>
                            </Link>
                            <button className="rgb-button-white hidden"
                                    style={{ width: "min(201px, 25.125vw)", height: "min(64px, 8vw)" }}
                                    onClick={() => this.importToken()}
                            >Import Token</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private async importToken(): Promise<void> {
        try {
            await LiskClient.getInstance().importToken();
        } catch {
            this.props.alertCb({
                message: "Failed to import token",
                severity: "error",
            });
        }
    }

    private calculateRowMarginTop(screenWidth: number): string {
        if (screenWidth > 1100) {
            return "-130px";
        }

        return "-180px";
    }

    private calculateRowPaddingTop(screenWidth: number): string {
        if (screenWidth > 1100) {
            return "400px";
        }

        return Math.min(400, linearInterpolateY(screenWidth, {x: 492, y: 300}, {x: 1100, y: 400})) + "px";
    }

    private calculateRowPaddingBottom(screenWidth: number): string {
        if (screenWidth > 1100) {
            return "550px";
        }

        return linearInterpolateY(screenWidth, {x: 496, y: 350}, {x: 1100, y: 480}) + "px";
    }
}
